import type { OpsUsageOverviewResponse } from '@nadoutong/contracts';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOpsUsageOverview, type OpsUsageWindow } from '../api/opsUsage.ts';
import { formatUsdCents } from '../components/formatters.ts';
import { UtcDateTimeInput } from '../components/UtcDateTimeInput.tsx';

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');
const CNY_FORMATTER = new Intl.NumberFormat('zh-CN', {
  style: 'currency',
  currency: 'CNY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatTokens(n: number): string {
  return TOKEN_FORMATTER.format(n);
}

function formatCnyCents(cents: number, signed = false): string {
  const value = CNY_FORMATTER.format(Math.abs(cents) / 100);
  if (!signed || cents === 0) return value;
  return cents > 0 ? `+${value}` : `-${value}`;
}

function formatPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

interface Props {
  readonly variant?: 'overview' | 'usage';
  readonly accountId?: string;
}

export function OpsUsagePage({ variant = 'usage', accountId }: Props): JSX.Element {
  const [params, setParams] = useSearchParams();
  const window = parseWindow(params.get('window'));
  const effectiveAccountId = accountId ?? params.get('accountId') ?? undefined;
  const email = accountId === undefined ? params.get('email') ?? undefined : undefined;
  const from = window === 'custom' ? params.get('from') ?? undefined : undefined;
  const to = window === 'custom' ? params.get('to') ?? undefined : undefined;
  const query = useOpsUsageOverview({
    window,
    from,
    to,
    accountId: effectiveAccountId,
    email,
  });

  function setWindow(next: OpsUsageWindow): void {
    const clone = new URLSearchParams(params);
    clone.set('window', next);
    if (next !== 'custom') {
      clone.delete('from');
      clone.delete('to');
    }
    setParams(clone, { replace: true });
  }

  function setCustomBound(key: 'from' | 'to', value: string): void {
    const clone = new URLSearchParams(params);
    clone.set('window', 'custom');
    if (value.length > 0) clone.set(key, value);
    else clone.delete(key);
    setParams(clone, { replace: true });
  }

  const title = variant === 'overview' ? '业务总览' : '用量总览';
  const subtitle = variant === 'overview'
    ? '收入、用量、利润和客户侧异常的运营入口。'
    : '按客户、模型、上游和密钥查看真实 API 用量。';

  return (
    <section className="flex flex-col gap-8" data-testid={variant === 'overview' ? 'ops-overview-page' : 'ops-usage-page'}>
      <header className="flex flex-col gap-2">
        <h1 className="text-display-sm font-semibold text-on-surface">{title}</h1>
        <p className="text-sm text-outline">{subtitle}</p>
      </header>

      <div className="flex flex-wrap items-end gap-2" role="tablist" aria-label="usage window">
        {(['today', '7d', '30d', 'custom'] as const).map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setWindow(item)}
            className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] ${
              window === item
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'
            }`}
            data-testid={`ops-usage-window-${item}`}
          >
            {item === 'today' ? '今日' : item === 'custom' ? '自定义' : item}
          </button>
        ))}
        <UtcDateTimeInput
          label="From UTC"
          value={params.get('from') ?? ''}
          onChange={(value) => setCustomBound('from', value)}
          inputClassName="w-56"
        />
        <UtcDateTimeInput
          label="To UTC"
          value={params.get('to') ?? ''}
          onChange={(value) => setCustomBound('to', value)}
          inputClassName="w-56"
        />
      </div>

      {query.isLoading ? (
        <div className="rounded-lg bg-surface-container p-6 text-sm text-outline" data-testid="ops-usage-loading">
          正在加载用量…
        </div>
      ) : query.isError ? (
        <div className="rounded-lg bg-error-container p-6 text-sm text-on-error-container" data-testid="ops-usage-error">
          无法加载用量：{query.error instanceof Error ? query.error.message : 'unknown error'}
        </div>
      ) : query.data ? (
        <UsageReady data={query.data} />
      ) : (
        <div className="rounded-lg bg-surface-container p-6 text-sm text-outline" data-testid="ops-usage-empty">
          当前窗口没有用量。
        </div>
      )}
    </section>
  );
}

function UsageReady({ data }: { readonly data: OpsUsageOverviewResponse }): JSX.Element {
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="请求数" value={formatTokens(data.totals.requestCount)} sub={`成功 ${formatTokens(data.totals.successCount)} · 失败 ${formatTokens(data.totals.errorCount)}`} />
        <MetricCard title="Tokens" value={formatTokens(data.totals.inputTokens + data.totals.outputTokens)} sub={`输入 ${formatTokens(data.totals.inputTokens)} · 输出 ${formatTokens(data.totals.outputTokens)}`} />
        <MetricCard title="缓存 Tokens" value={formatTokens(data.totals.cacheReadTokens + data.totals.cacheWriteTokens)} sub={`命中 ${formatTokens(data.totals.cacheReadTokens)} · 写入 ${formatTokens(data.totals.cacheWriteTokens)}`} />
        <MetricCard title="用户收费" value={formatUsdCents(data.totals.usdChargedCents)} sub={`Gateway ${formatUsdCents(data.totals.costUsdCents)}`} />
        <MetricCard title="上游成本" value={formatCnyCents(data.totals.cnyUpstreamCostCents)} sub="revenue ledger" />
        <MetricCard title="L2 利润" value={formatCnyCents(data.totals.l2ProfitCnyCents, true)} sub={`毛利率 ${formatPct(data.totals.l2MarginPct)}`} />
        <MetricCard title="总毛利" value={formatCnyCents(data.totals.totalGrossMarginCnyCents, true)} sub="L1 + L2" />
        <MetricCard title="窗口" value={formatWindow(data.window.from, data.window.to)} sub="UTC" />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <TenantsTable data={data} />
        <ModelsTable data={data} />
        <UpstreamsTable data={data} />
        <KeysTable data={data} />
      </div>
    </>
  );
}

function MetricCard({ title, value, sub }: { readonly title: string; readonly value: string; readonly sub: string }): JSX.Element {
  return (
    <article className="rounded-lg bg-surface-container p-5" data-testid={`ops-usage-metric-${title}`}>
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-outline">{title}</p>
      <p className="mt-3 font-mono text-2xl font-semibold text-on-surface">{value}</p>
      <p className="mt-2 text-xs text-on-surface-variant">{sub}</p>
    </article>
  );
}

function TenantsTable({ data }: { readonly data: OpsUsageOverviewResponse }): JSX.Element {
  return (
    <UsageTable title="Top 租户" empty="暂无租户用量">
      {data.tenants.map((row) => (
        <tr key={row.accountId} className="bg-surface-container-low">
          <td className="px-4 py-3">
            <div className="font-semibold text-on-surface">{row.email || row.accountId}</div>
            <div className="font-mono text-[11px] text-outline">{row.accountId}</div>
          </td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.requestCount)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.inputTokens + row.outputTokens)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatUsdCents(row.usageUsdChargedCents)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatCnyCents(row.l2ProfitCnyCents, true)}</td>
        </tr>
      ))}
    </UsageTable>
  );
}

function ModelsTable({ data }: { readonly data: OpsUsageOverviewResponse }): JSX.Element {
  return (
    <UsageTable title="按模型" empty="暂无模型用量">
      {data.models.map((row) => (
        <tr key={`${row.vendor}:${row.model}`} className="bg-surface-container-low">
          <td className="px-4 py-3">
            <div className="font-semibold text-on-surface">{row.model}</div>
            <div className="text-[11px] text-outline">{row.vendor}</div>
          </td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.requestCount)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.inputTokens + row.outputTokens)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatUsdCents(row.usageUsdChargedCents)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatCnyCents(row.l2ProfitCnyCents, true)}</td>
        </tr>
      ))}
    </UsageTable>
  );
}

function UpstreamsTable({ data }: { readonly data: OpsUsageOverviewResponse }): JSX.Element {
  return (
    <UsageTable title="按上游" empty="暂无上游用量">
      {data.upstreams.map((row) => (
        <tr key={row.upstreamId} className="bg-surface-container-low">
          <td className="px-4 py-3">
            <div className="font-semibold text-on-surface">{row.upstreamName || row.upstreamId}</div>
            <div className="font-mono text-[11px] text-outline">{row.upstreamId}</div>
          </td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.requestCount)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.inputTokens + row.outputTokens)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatUsdCents(row.costUsdCents)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.errorCount)}</td>
        </tr>
      ))}
    </UsageTable>
  );
}

function KeysTable({ data }: { readonly data: OpsUsageOverviewResponse }): JSX.Element {
  return (
    <UsageTable title="按密钥" empty="暂无密钥用量">
      {data.keys.map((row) => (
        <tr key={row.apiKeyId} className="bg-surface-container-low">
          <td className="px-4 py-3">
            <div className="font-semibold text-on-surface">{row.apiKeyName ?? '未命名密钥'}</div>
            <div className="text-[11px] text-outline">{row.channelName ?? row.channelSlug ?? '未知渠道'}</div>
          </td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.requestCount)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.cacheReadTokens)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatTokens(row.cacheWriteTokens)}</td>
          <td className="px-4 py-3 text-right font-mono">{formatUsdCents(row.costUsdCents)}</td>
        </tr>
      ))}
    </UsageTable>
  );
}

function UsageTable({ title, empty, children }: { readonly title: string; readonly empty: string; readonly children: ReactNode }): JSX.Element {
  const count = Array.isArray(children) ? children.length : 1;
  return (
    <article className="overflow-hidden rounded-lg bg-surface-container p-5">
      <h2 className="text-base font-semibold text-on-surface">{title}</h2>
      {count === 0 ? (
        <p className="mt-4 text-sm text-outline">{empty}</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-separate border-spacing-y-1 text-sm">
            <thead className="text-xs uppercase tracking-[0.12em] text-outline">
              <tr>
                <th className="px-4 py-2 text-left">对象</th>
                <th className="px-4 py-2 text-right">请求</th>
                <th className="px-4 py-2 text-right">Tokens/缓存</th>
                <th className="px-4 py-2 text-right">费用/写入</th>
                <th className="px-4 py-2 text-right">利润/错误</th>
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </article>
  );
}

function parseWindow(raw: string | null): OpsUsageWindow {
  return raw === 'today' || raw === '30d' || raw === 'custom' ? raw : '7d';
}

function formatWindow(from: string, to: string): string {
  return `${from.slice(5, 10)} → ${to.slice(5, 10)}`;
}
